import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Share, Plus, Smartphone, Download } from "lucide-react";

const STORAGE_KEY = "bf_install_prompt";

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
};

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
};

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    if (localStorage.getItem(STORAGE_KEY) === "dismissed") return;
    setIos(isIOS());

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS never fires beforeinstallprompt — show the chip if Safari + not installed
    if (isIOS()) {
      const t = setTimeout(() => setVisible(true), 6000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onPrompt);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismissForever = () => {
    localStorage.setItem(STORAGE_KEY, "dismissed");
    setVisible(false);
    setSheet(false);
  };

  const doInstall = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") {
        localStorage.setItem(STORAGE_KEY, "installed");
      }
      setDeferred(null);
      setVisible(false);
    } else if (ios) {
      setSheet(true);
    }
  };

  if (!visible) return null;

  return (
    <>
      {/* Floating chip above bottom nav */}
      <motion.button
        data-testid="install-chip"
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        onClick={doInstall}
        className="fixed bottom-24 left-5 z-40 rounded-full pl-2 pr-4 h-11 flex items-center gap-2 press shadow-lg"
        style={{ background: "var(--ink)", color: "var(--bg)" }}
      >
        <span
          className="w-7 h-7 rounded-full grid place-items-center"
          style={{ background: "var(--bg)", color: "var(--ink)" }}
        >
          <Download size={13} strokeWidth={2} />
        </span>
        <span className="text-[11px] tracking-editorial-sm uppercase">Install app</span>
        <button
          data-testid="install-chip-dismiss"
          onClick={(e) => { e.stopPropagation(); dismissForever(); }}
          className="ml-1 opacity-70 hover:opacity-100"
          aria-label="Dismiss"
        >
          <X size={13} />
        </button>
      </motion.button>

      {/* iOS instructions sheet */}
      <AnimatePresence>
        {sheet && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 bg-black/40"
            onClick={() => setSheet(false)}
            data-testid="install-ios-sheet"
          >
            <motion.div
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 32, stiffness: 260 }}
              className="absolute bottom-0 inset-x-0 rounded-t-3xl p-6 pb-8"
              style={{ background: "var(--paper)" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className="w-9 h-9 rounded-full grid place-items-center"
                    style={{ background: "var(--accent)", color: "var(--bg)" }}
                  >
                    <Smartphone size={15} strokeWidth={1.5} />
                  </div>
                  <div className="text-[9px] tracking-editorial uppercase" style={{ color: "var(--ink-3)" }}>Install on iPhone</div>
                </div>
                <button
                  data-testid="ios-close"
                  onClick={() => setSheet(false)}
                  className="w-8 h-8 rounded-full grid place-items-center press"
                >
                  <X size={14} />
                </button>
              </div>

              <h3 className="font-display text-[22px] leading-[1.1] mt-4" style={{ color: "var(--ink)" }}>
                Add Butterfly to your
                <br />
                <span className="font-display-italic" style={{ color: "var(--accent)" }}>home screen.</span>
              </h3>

              <ol className="mt-5 space-y-3">
                <IosStep
                  n="1"
                  title="Open in Safari"
                  body="If you're inside another app (like WhatsApp), tap the ••• menu and choose 'Open in Safari'."
                />
                <IosStep
                  n="2"
                  icon={<Share size={14} strokeWidth={1.8} />}
                  title="Tap the Share icon"
                  body="It's the square with an ↑ at the bottom (or top) of Safari."
                />
                <IosStep
                  n="3"
                  icon={<Plus size={14} strokeWidth={1.8} />}
                  title="Choose 'Add to Home Screen'"
                  body="Scroll if you don't see it. Then tap 'Add' in the top-right."
                />
              </ol>

              <button
                data-testid="ios-done"
                onClick={dismissForever}
                className="mt-5 w-full h-12 rounded-full text-[12px] tracking-editorial-sm uppercase press"
                style={{ background: "var(--ink)", color: "var(--bg)" }}
              >
                Got it
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

const IosStep = ({ n, title, body, icon }) => (
  <li className="flex items-start gap-3">
    <div
      className="w-8 h-8 rounded-full grid place-items-center shrink-0 text-[11px]"
      style={{ background: "var(--bg-alt)", color: "var(--ink)" }}
    >
      {icon || n}
    </div>
    <div>
      <div className="font-display text-[15px]" style={{ color: "var(--ink)" }}>{title}</div>
      <div className="text-[11px] mt-0.5" style={{ color: "var(--ink-2)" }}>{body}</div>
    </div>
  </li>
);
